import React, { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { DevTools } from "jotai-devtools";
import "jotai-devtools/styles.css";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./styles/cookie-consent.css";
import { cookieConsentConfig } from "./config/cookie-consent";
import { Footer, FetchProgressBar } from "./components/Shared";
import { Navbar } from "./components/Navbar";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { FaExclamationTriangle } from "./utils/icons";
import NotFound from "./components/NotFound";
import Worklist from "./components/Worklist";
import { AuthProvider } from "./context/AuthContext";
import { BatchUploadProvider } from "./context/BatchUploadContext";
import { registerAuthQueryClient } from "./services/auth";
import Login from "./components/Login";
import AuthenticatedHome from "./components/AuthenticatedHome";
import OAuthCallback from "./components/OAuthCallback";
import ChangePassword from "./components/ChangePassword";
import MyTemplates from "./components/MyTemplates";
import { PrivateRoute, AdminRoute, UploadRoute } from "./components/PrivateRoute";
import SetPassword from "./components/SetPassword";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AgreementGate from "./components/AgreementGate";
import AdminUserManagement from "./components/AdminUserManagement";
import IntegrationStatus from "./components/IntegrationStatus";
import ReaderStudyLiveData from "./components/ReaderStudyLiveData";
import ReaderStudyProjects from "./components/ReaderStudyProjects";
import KeyImageReview from "./components/reviewer/KeyImageReview";
import AdminFeedback from "./components/AdminFeedback";
import ContentLibrary from "@/components/contentLibrary/ContentLibrary";
import SurfacerScoreboard from "./components/SurfacerScoreboard";
import CompaniesPage from "./components/CompaniesPage";
import CompanyDetailPage from "./components/CompanyDetailPage";
import UploadStudyPage from "./components/UploadStudyPage";
import CohortCaseViewer from "./components/CohortCaseViewer";
import TriageWorklistPage from "./components/triage/TriageWorklistPage";
import TriageCasePage from "./components/triage/TriageCasePage";
import DevicePanelListPage from "./components/devicePanel/DevicePanelListPage";
import DevicePanelCasePage from "./components/devicePanel/DevicePanelCasePage";
import JointReviewListPage from "./components/jointReview/JointReviewListPage";
import JointReviewCasePage from "./components/jointReview/JointReviewCasePage";
import { LabelingPage } from "./components/LabelingPage";
import { CategoryHome } from "./components/labeling/overview/CategoryHome";
import { CategoryView } from "./components/labeling/overview/CategoryView";
import { OTStudyHome } from "./components/labeling/OTStudyHome";
import { SegStudyHome } from "./components/labeling/SegStudyHome";
import { ReportingStudyHome } from "./components/labeling/ReportingStudyHome";
import { SegmentationStudyHome } from "./components/labeling/SegmentationStudyHome";
import PrototypeReport from "./components/PrototypeReport";
import { WorkerProvider } from "./context/WorkerContext";
import SessionExpiredModal from "./components/SessionExpiredModal";
import { HelpDialog } from "./components/HelpDialog";
import { PageToast } from "./components/PageToast";
import { BatchUploadMonitor } from "./components/upload/BatchUploadMonitor";
import { FeedbackHost } from "./components/feedback/FeedbackHost";
import { DiagnosticDialog } from "./components/viewport/DiagnosticDialog";
import { ClearAllMeasurementsDialog } from "./components/ClearAllMeasurementsDialog";
import { useSetAtom } from "jotai";
import { helpDialogOpenAtom, diagnosticDialogOpenAtom } from "./atoms/measurementAtoms";
import NotifyLayout from "./notify/NotifyLayout";
import NotifyCaseList from "./notify/NotifyCaseList";
import NotifyCaseDetail from "./notify/NotifyCaseDetail";
import { isNotifyPath } from "./notify/paths";
import { useNotifyOpen } from "./notify/useNotifyOpen";

// Configure TanStack Query client with defaults optimized for medical imaging app
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - unused data kept in cache
      refetchOnWindowFocus: false, // Don't refetch unexpectedly in medical app
      retry: 1, // Single retry on failure
    },
  },
});
registerAuthQueryClient(queryClient);

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex items-center justify-center min-h-screen bg-bg">
      <div className="bg-surface border border-border p-8 max-w-md w-full">
        <div className="flex items-center mb-4">
          <span className="text-warn text-3xl mr-3">
            <FaExclamationTriangle />
          </span>
          <h2 className="text-2xl font-bold text-fg">Oops! Something went wrong</h2>
        </div>
        <p className="text-fg-muted mb-4">
          We're sorry, but an error occurred while processing your request.
        </p>
        <div className="bg-warn-tint border-l-4 border-warn p-4 mb-4">
          <p className="font-bold text-warn">Error details:</p>
          <p className="text-sm text-fg-muted">{message}</p>
        </div>
        <button
          onClick={resetErrorBoundary}
          className="bg-signal hover:bg-signal/90 text-fg font-medium py-2 px-4 rounded-[2px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal-ring w-full transition-colors duration-200"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const setHelpDialogOpen = useSetAtom(helpDialogOpenAtom);
  const setDiagnosticDialogOpen = useSetAtom(diagnosticDialogOpenAtom);

  useEffect(() => {
    CookieConsent.run(cookieConsentConfig);
  }, []);

  // Global hotkeys — native listener so they fire regardless of focus state
  // or which page is active. Both skip when an editable element has focus.
  //   Shift+?              → help dialog (keyboard shortcut reference)
  //   Cmd/Ctrl+Shift+D     → diagnostic dialog (GPU verdict, renderer, cache)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const editable =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
      if (editable) return;

      if (e.shiftKey && e.key === "?") {
        e.preventDefault();
        setHelpDialogOpen(true);
        return;
      }
      // Match key case-insensitively — shift normalizes "d" → "D" on most
      // browsers, but defensively handle either.
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "D" || e.key === "d")) {
        // Browser default for Cmd/Ctrl+Shift+D is "Bookmark all tabs" — we
        // override it because the radiologist workflow doesn't use Chrome
        // bookmarks and the diagnostic shortcut is mnemonic.
        e.preventDefault();
        setDiagnosticDialogOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [setHelpDialogOpen, setDiagnosticDialogOpen]);

  const location = useLocation();
  // A tapped Notify alert routes here from any page, e.g. /worklist: the Notify
  // service worker can't navigate windows it doesn't control, so it asks.
  useNotifyOpen();
  // The installed Notify app (/notify/*) has its own header: no web-demo chrome.
  // isNotifyPath is the rule shared with PrivateRoute and sw.js.
  const inNotify = isNotifyPath(location.pathname);

  return (
    <QueryClientProvider client={queryClient}>
      <WorkerProvider>
        <AuthProvider>
          <BatchUploadProvider>
            <SessionExpiredModal />
            <HelpDialog />
            <DiagnosticDialog />
            <ClearAllMeasurementsDialog />
            <PageToast />
            <FeedbackHost />
            <BatchUploadMonitor />
            <>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <div className="flex flex-col h-screen bg-bg overflow-hidden">
                  {!inNotify &&
                    ![
                      "/login",
                      "/set-password",
                      "/agreement",
                      "/forgot-password",
                      "/reset-password",
                    ].includes(location.pathname) && <Navbar />}
                  <ImpersonationBanner />
                  <FetchProgressBar />
                  <main className="flex-1 min-h-0 overflow-auto">
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/oauth/callback" element={<OAuthCallback />} />
                      <Route path="/set-password" element={<SetPassword />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route
                        path="/agreement"
                        element={
                          <PrivateRoute>
                            <AgreementGate />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/change-password"
                        element={
                          <PrivateRoute>
                            <ChangePassword />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/my-templates"
                        element={
                          <PrivateRoute>
                            <MyTemplates />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/"
                        element={
                          <PrivateRoute>
                            <AuthenticatedHome />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/upload"
                        element={
                          <UploadRoute>
                            <UploadStudyPage />
                          </UploadRoute>
                        }
                      />
                      <Route
                        path="/worklist"
                        element={
                          <PrivateRoute>
                            <Worklist />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/triage/:worklistId"
                        element={
                          <PrivateRoute>
                            <TriageWorklistPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/triage/:worklistId/case/:studyUid"
                        element={
                          <PrivateRoute>
                            <TriageCasePage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/panel/:panelId"
                        element={
                          <PrivateRoute>
                            <DevicePanelListPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/panel/:panelId/case/:studyUid"
                        element={
                          <PrivateRoute>
                            <DevicePanelCasePage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/case-viewer/*"
                        element={
                          <PrivateRoute>
                            <Worklist viewMode="case" />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/reporter/*"
                        element={
                          <PrivateRoute>
                            <Worklist viewMode="reporter" />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/cohort-viewer"
                        element={
                          <PrivateRoute>
                            <CohortCaseViewer />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/review/key-images/:assessmentId"
                        element={
                          <PrivateRoute>
                            <KeyImageReview />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/review/cohort/:cohortId"
                        element={
                          <PrivateRoute>
                            <Worklist viewMode="review" />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling-overview"
                        element={
                          <PrivateRoute>
                            <CategoryHome />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling-overview/:category"
                        element={
                          <PrivateRoute>
                            <CategoryView />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling-overview/:category/:projectId"
                        element={
                          <PrivateRoute>
                            <CategoryView />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling/ot"
                        element={
                          <PrivateRoute>
                            <OTStudyHome />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling/urinary-stone"
                        element={
                          <PrivateRoute>
                            <SegStudyHome />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling/reporting/:projectId"
                        element={
                          <PrivateRoute>
                            <ReportingStudyHome />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling/joint-review/:projectId"
                        element={
                          <PrivateRoute>
                            <JointReviewListPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling/joint-review/:projectId/case/:studyUid"
                        element={
                          <PrivateRoute>
                            <JointReviewCasePage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling/segmentation-study/:projectId"
                        element={
                          <PrivateRoute>
                            <SegmentationStudyHome />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/labeling"
                        element={
                          <PrivateRoute>
                            <LabelingPage />
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/admin/content-library"
                        element={
                          <AdminRoute>
                            <ContentLibrary />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <AdminRoute>
                            <AdminUserManagement />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/upload_status"
                        element={
                          <AdminRoute>
                            <IntegrationStatus />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/reader-study"
                        element={
                          <AdminRoute>
                            <ReaderStudyProjects />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/reader-study/:projectId"
                        element={
                          <AdminRoute>
                            <ReaderStudyLiveData />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/feedback"
                        element={
                          <AdminRoute>
                            <AdminFeedback />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/surfacer-scoreboard/:cohortId?"
                        element={
                          <AdminRoute>
                            <SurfacerScoreboard />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/companies"
                        element={
                          <AdminRoute>
                            <CompaniesPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/admin/companies/:companyId"
                        element={
                          <AdminRoute>
                            <CompanyDetailPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/prototype-report/*"
                        element={<PrototypeReport sessionMode="demo" templateMode="ai" />}
                      />
                      <Route
                        path="/notify"
                        element={
                          <PrivateRoute>
                            <NotifyLayout />
                          </PrivateRoute>
                        }
                      >
                        <Route index element={<NotifyCaseList />} />
                        <Route path="cases/:siteId/:studyUid" element={<NotifyCaseDetail />} />
                        <Route path="*" element={<NotFound />} />
                      </Route>
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  {!inNotify && <Footer />}
                </div>
              </ErrorBoundary>
            </>
          </BatchUploadProvider>
        </AuthProvider>
      </WorkerProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      <DevTools />
    </QueryClientProvider>
  );
};

export default App;
